/**
 * Clip Controller
 * Manages clip navigation, state, and lifecycle
 */

import appState from '../../core/app-state.js';
import * as QuestionManager from '../questions/question-manager.js';
import * as TranscriptModule from './transcript.js';

/**
 * Reset clip state when navigating
 * Cleans up audio, transcript, and question state
 */
export function resetClipState(): void {
  // Cancel any active auto-advance
  QuestionManager.cancelAutoAdvance();
  
  // Clear depth tracking for the current clip if we have one
  if (appState.activeClipId) {
    appState.clipDepth.delete(appState.activeClipId);
  }
  
  // Pause and reset audio
  const audio = document.querySelector('audio');
  if (audio) {
    audio.pause();
    audio.currentTime = 0;
    
    // Remove any existing play event listeners to avoid stacking
    (audio as any).removeEventListener('play', (audio as any)._transcriptGatingHandler);
    delete (audio as any)._transcriptGatingHandler;
    
    // Remove custom player event listeners
    if ((audio as any)._playerEventListeners) {
      for (const [event, listener] of Object.entries((audio as any)._playerEventListeners)) {
        audio.removeEventListener(event, listener as EventListener);
      }
      delete (audio as any)._playerEventListeners;
    }
  }
  
  // Clear feedback
  const feedbackRegion = document.getElementById('feedback-region');
  if (feedbackRegion) {
    feedbackRegion.innerHTML = '';
  }
  
  // We don't clear answer tracking when navigating
  // This preserves answers for each clip when returning to them
  
  // Reset the transcript injected flag
  appState.transcriptInjected = false;
  
  // Reset transcript panel state
  appState.transcriptPanelOpen = false;
  
  // Use the dedicated function for transcript cleanup that handles all event listeners
  TranscriptModule.removeTranscriptEventListeners();
  
  // Ensure window._transcriptElements is cleared (for extra safety)
  (window as any)._transcriptElements = null;  
  
  // Reset question index
  appState.currentQuestionIndex = 0;
}

/**
 * Update navigation controls based on current state
 */
export function updateNavControls(): void {
  const navControls = document.getElementById('nav-controls');
  if (!navControls) return;
  
  // Clear existing controls (bottom navigation removed per requirements)
  navControls.innerHTML = '';
  
  // Previous/next navigation removed - users navigate via Library view
}

/**
 * Create navigation controls
 */
export function renderNavControls(): void {
  updateNavControls();
}

/**
 * Handle navigation to previous clip
 */
export function handlePreviousClip(
  renderClip: (idx: number) => void,
  switchMode: (mode: string) => void
): void {
  if (appState.currentClipIdx > 0) {
    resetClipState();
    appState.currentClipIdx--;
    renderClip(appState.currentClipIdx);
    
    // Ensure we're in exercise mode
    switchMode('exercise');
  }
}

/**
 * Handle navigation to next clip
 */
export function handleNextClip(
  renderClip: (idx: number) => void,
  switchMode: (mode: string) => void
): void {
  if (appState.currentClipIdx < appState.clips.length - 1) {
    resetClipState();
    appState.currentClipIdx++;
    renderClip(appState.currentClipIdx);
    
    // Ensure we're in exercise mode
    switchMode('exercise');
  }
}

/**
 * Reset questions for "Try again" functionality
 */
export function resetClipQuestions(
  clip: any,
  clipIndex: number,
  renderCurrentQuestion: (clip: any, idx: number) => void
): void {
  // Reset question index to start at the first question
  appState.currentQuestionIndex = 0;
  
  // Clear answer tracking for this clip
  if (clip && clip.id && appState.clipAnswers.has(clip.id)) {
    appState.clipAnswers.set(clip.id, []);
  }
  
  // Clear the feedback region
  const feedbackRegion = document.getElementById('feedback-region');
  if (feedbackRegion) {
    feedbackRegion.innerHTML = '';
    feedbackRegion.setAttribute('aria-hidden', 'true');
  }
  
  // Render the first question
  renderCurrentQuestion(clip, clipIndex);
  
  // Announce to screen readers
  TranscriptModule.announceToScreenReader('Questions reset. Starting from the beginning of the clip.');
}
